import { Box, Typography, Paper, Grid, CircularProgress } from "@mui/material";
import { FC, useEffect, useState } from "react";
import HttpService from "../../Hooks/Https-services";
import { APIRoutes } from "../../Utils/constant";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface AggregateStat {
  tenant: string;
  total: number;
  pending: number;
  completed: number;
  date: string;
}

interface Error {
  id: number;
  game_id: number;
  date: string;
  tenant: string;
  ingest_status: string;
  ingest_status_timestamp: string;
  video_status: string;
  video_status_timestamp: string;
  enrichment_status: string;
  enrichment_status_timestamp: string;
}

const Dashboard: FC = () => {
  const [aggregateStats, setAggregateStats] = useState<AggregateStat[]>([]);
  const [errors, setErrors] = useState<Error[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorLoading, setErrorLoading] = useState(true);

  useEffect(() => {
    fetchAggregateStats();
    fetchErrors();
  }, []);

  const fetchAggregateStats = async () => {
    try {
      const response = await HttpService.get(
        APIRoutes.GetAggregateStats + "?days=7"
      );
      setAggregateStats(response.data.data || []);
    } catch (error) {
      console.error("Error fetching aggregate stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrors = async () => {
    try {
      const response = await HttpService.get(APIRoutes.GetErrors + "?limit=10");
      setErrors(response.data.data || []);
    } catch (error) {
      console.error("Error fetching errors:", error);
    } finally {
      setErrorLoading(false);
    }
  };

  // Group stats by tenant
  const statsByTenant = aggregateStats.reduce((acc, stat) => {
    if (!acc[stat.tenant]) {
      acc[stat.tenant] = {
        tenant: stat.tenant,
        total: 0,
        pending: 0,
        completed: 0,
      };
    }
    acc[stat.tenant].total += stat.total;
    acc[stat.tenant].pending += stat.pending;
    acc[stat.tenant].completed += stat.completed;
    return acc;
  }, {} as Record<string, { tenant: string; total: number; pending: number; completed: number }>);

  const tenantStatsArray = Object.values(statsByTenant);

  // Prepare data for the stacked bar chart by tenant
  const chartDataByTenant = Object.entries(
    aggregateStats.reduce((acc, stat) => {
      if (!acc[stat.date]) {
        acc[stat.date] = { date: stat.date };
      }
      acc[stat.date][`${stat.tenant}_total`] = stat.total;
      acc[stat.date][`${stat.tenant}_pending`] = stat.pending;
      acc[stat.date][`${stat.tenant}_completed`] = stat.completed;
      return acc;
    }, {} as Record<string, any>)
  )
    .map(([_, value]) => value)
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .slice(-7); // Last 7 days

  const getTotalGames = () => {
    return tenantStatsArray.reduce((sum, tenant) => sum + tenant.total, 0);
  };

  const getPendingGames = () => {
    return tenantStatsArray.reduce((sum, tenant) => sum + tenant.pending, 0);
  };

  const getCompletedGames = () => {
    return tenantStatsArray.reduce((sum, tenant) => sum + tenant.completed, 0);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="primary">
              Total Games
            </Typography>
            {loading ? (
              <CircularProgress size={40} sx={{ mt: 2 }} />
            ) : (
              <Typography variant="h3" sx={{ mt: 2 }}>
                {getTotalGames()}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="warning.main">
              Pending Games
            </Typography>
            {loading ? (
              <CircularProgress size={40} sx={{ mt: 2 }} />
            ) : (
              <Typography variant="h3" sx={{ mt: 2 }}>
                {getPendingGames()}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="success.main">
              Completed Games
            </Typography>
            {loading ? (
              <CircularProgress size={40} sx={{ mt: 2 }} />
            ) : (
              <Typography variant="h3" sx={{ mt: 2 }}>
                {getCompletedGames()}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Games by Tenant Chart */}
      {!loading && chartDataByTenant.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6">Games by Tenant (Last 7 Days)</Typography>
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#8884d8",
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">Total</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#ffc658",
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">Pending</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#82ca9d",
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">Completed</Typography>
              </Box>
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartDataByTenant}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="NAHL_total"
                stackId="nahl"
                fill="#8884d8"
                name="NAHL"
              />
              <Bar
                dataKey="NAHL_pending"
                stackId="nahl"
                fill="#ffc658"
                legendType="none"
              />
              <Bar
                dataKey="NAHL_completed"
                stackId="nahl"
                fill="#82ca9d"
                legendType="none"
              />
              <Bar
                dataKey="USAH_total"
                stackId="usah"
                fill="#8884d8"
                name="USAH"
              />
              <Bar
                dataKey="USAH_pending"
                stackId="usah"
                fill="#ffc658"
                legendType="none"
              />
              <Bar
                dataKey="USAH_completed"
                stackId="usah"
                fill="#82ca9d"
                legendType="none"
              />
              <Bar
                dataKey="SSM_total"
                stackId="ssm"
                fill="#8884d8"
                name="SSM"
              />
              <Bar
                dataKey="SSM_pending"
                stackId="ssm"
                fill="#ffc658"
                legendType="none"
              />
              <Bar
                dataKey="SSM_completed"
                stackId="ssm"
                fill="#82ca9d"
                legendType="none"
              />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Errors Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Recent Errors
        </Typography>
        {errorLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : errors.length > 0 ? (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2, fontWeight: "bold" }}>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Game ID</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Tenant</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Video Status</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Ingest Status</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Enrichment Status</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="subtitle2">Date</Typography>
              </Grid>
            </Grid>
            {errors.map((error) => (
              <Grid
                container
                spacing={2}
                key={error.id}
                sx={{ py: 1, borderBottom: "1px solid #eee" }}
              >
                <Grid item xs={2}>
                  <Typography variant="body2">{error.game_id}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2">{error.tenant}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography
                    variant="body2"
                    color={
                      error.video_status === null ? "warning.main" : "error"
                    }
                  >
                    {error.video_status || "NULL"}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2">{error.ingest_status}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2">
                    {error.enrichment_status || "NULL"}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2">{error.date}</Typography>
                </Grid>
              </Grid>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No errors found
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;
